export const STORAGE_KEY = 'hibiki-progress-v2';
export const LEGACY_STORAGE_KEY = 'hibiki-progress';

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const clone = value => value === undefined ? undefined : JSON.parse(JSON.stringify(value));

function emptyExposure(now = Date.now()) {
  return {since: now, words: {}, characters: {}, stories: {}};
}

export function createLanguageProgress(now = Date.now()) {
  return {
    prefs: {english: true, reading: true},
    records: {translation: {}, target: {}},
    sessions: [],
    learnIndex: 0,
    storyPrefs: {speed: 1, english: false, transcript: false},
    exposure: emptyExposure(now),
    tracks: {
      study: {reviewedWordIds: [], lessonId: null},
      audio: {programs: {}},
      stories: {},
    },
    listeningSeconds: 0,
    listeningSince: now,
    lastActivity: null,
  };
}

function normalizeSession(session) {
  const normalized = clone(session) ?? {};
  if (!normalized.answerMode) {
    if (normalized.language === 'en') normalized.answerMode = 'translation';
    if (normalized.language === 'ja') normalized.answerMode = 'target';
  }
  return normalized;
}

function normalizeExposure(exposure, now) {
  if (!isObject(exposure)) return emptyExposure(now);
  return {
    ...clone(exposure),
    since: exposure.since ?? now,
    words: isObject(exposure.words) ? clone(exposure.words) : {},
    characters: isObject(exposure.characters) ? clone(exposure.characters) : {},
    stories: isObject(exposure.stories) ? clone(exposure.stories) : {},
  };
}

function normalizeLanguageProgress(source = {}, {preserveMissingExposure = false, now = Date.now()} = {}) {
  const base = createLanguageProgress(now);
  const value = isObject(source) ? source : {};
  const records = isObject(value.records) ? value.records : {};
  const tracks = isObject(value.tracks) ? value.tracks : {};
  const study = isObject(tracks.study) ? tracks.study : {};
  const audio = isObject(tracks.audio) ? tracks.audio : {};
  const progress = {
    ...clone(value),
    prefs: {...base.prefs, ...(isObject(value.prefs) ? clone(value.prefs) : {})},
    records: {
      translation: clone(records.translation ?? records.en ?? {}),
      target: clone(records.target ?? records.ja ?? {}),
    },
    sessions: Array.isArray(value.sessions) ? value.sessions.map(normalizeSession) : [],
    learnIndex: Number.isInteger(value.learnIndex) && value.learnIndex >= 0 ? value.learnIndex : 0,
    storyPrefs: {...base.storyPrefs, ...(isObject(value.storyPrefs) ? clone(value.storyPrefs) : {})},
    tracks: {
      study: {
        ...base.tracks.study,
        ...clone(study),
        reviewedWordIds: Array.isArray(study.reviewedWordIds) ? [...new Set(study.reviewedWordIds)] : [],
      },
      audio: {
        ...base.tracks.audio,
        ...clone(audio),
        programs: isObject(audio.programs) ? clone(audio.programs) : {},
      },
      stories: isObject(tracks.stories) ? clone(tracks.stories) : {},
    },
    listeningSeconds: Number.isFinite(value.listeningSeconds) && value.listeningSeconds >= 0 ? value.listeningSeconds : 0,
    listeningSince: Number.isFinite(value.listeningSince) ? value.listeningSince : now,
    lastActivity: value.lastActivity ?? null,
  };
  if (hasOwn(value, 'exposure')) progress.exposure = normalizeExposure(value.exposure, now);
  else if (preserveMissingExposure) delete progress.exposure;
  else progress.exposure = base.exposure;
  return progress;
}

function validateV2(value) {
  if (!isObject(value) || value.version !== 2 || !isObject(value.languages)) {
    throw new Error('Invalid Hibiki v2 progress');
  }
  if (value.selectedLanguageId !== null && value.selectedLanguageId !== undefined && typeof value.selectedLanguageId !== 'string') {
    throw new Error('Invalid Hibiki v2 selected language');
  }
}

export function migrateProgress(legacy) {
  if (isObject(legacy) && legacy.version === 2) {
    validateV2(legacy);
    return {
      ...clone(legacy),
      version: 2,
      selectedLanguageId: legacy.selectedLanguageId ?? null,
      languages: Object.fromEntries(Object.entries(legacy.languages).map(([id, progress]) => [
        id,
        normalizeLanguageProgress(progress, {preserveMissingExposure: !hasOwn(progress ?? {}, 'exposure')}),
      ])),
    };
  }
  if (legacy === null || legacy === undefined) return {version: 2, selectedLanguageId: null, languages: {}};
  if (!isObject(legacy)) throw new Error('Invalid legacy Hibiki progress');
  return {
    version: 2,
    selectedLanguageId: 'ja',
    languages: {
      ja: normalizeLanguageProgress(legacy, {preserveMissingExposure: !hasOwn(legacy, 'exposure')}),
    },
  };
}

function parseStored(raw, label) {
  try {
    return JSON.parse(raw);
  } catch (cause) {
    const error = new Error(`Corrupt ${label} progress`);
    error.cause = cause;
    throw error;
  }
}

export function loadProgress(storage) {
  const current = storage.getItem(STORAGE_KEY);
  if (current !== null) {
    const parsed = parseStored(current, 'v2');
    validateV2(parsed);
    return migrateProgress(parsed);
  }

  const legacy = storage.getItem(LEGACY_STORAGE_KEY);
  if (legacy === null) return migrateProgress(null);
  const migrated = migrateProgress(parseStored(legacy, 'legacy'));
  storage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  return migrated;
}

export function saveProgress(state, storage) {
  const normalized = migrateProgress(state);
  storage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

export function getLanguageProgress(state, id) {
  if (!isObject(state) || state.version !== 2 || !isObject(state.languages)) {
    throw new Error('A valid v2 progress state is required');
  }
  if (!state.languages[id]) state.languages[id] = createLanguageProgress();
  return state.languages[id];
}

function reviewRow() {
  return {storyAudio: 0, studyAudio: 0, storyReads: 0, studyReads: 0, reviews: 0, audioPlays: 0};
}

function reviewCharacters(text) {
  return new Set([...String(text ?? '')].filter(char => /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|[ー々]/u.test(char)));
}

export function ensureExposure(progress, deck, capabilities = {characters: true}) {
  if (isObject(progress.exposure)) {
    progress.exposure = normalizeExposure(progress.exposure, Date.now());
    return progress.exposure;
  }

  const exposure = emptyExposure();
  const includeCharacters = capabilities === true || capabilities?.characters !== false;
  for (const word of deck ?? []) {
    const attempts = ['translation', 'target'].reduce((sum, mode) => {
      const value = Number(progress.records?.[mode]?.[word.id]?.attempts ?? 0);
      return sum + (Number.isFinite(value) && value > 0 ? value : 0);
    }, 0);
    if (!attempts) continue;
    exposure.words[word.id] = {...reviewRow(), reviews: attempts};
    if (includeCharacters) {
      for (const character of reviewCharacters(word.word)) {
        const row = exposure.characters[character] ||= reviewRow();
        row.reviews += attempts;
      }
    }
  }
  progress.exposure = exposure;
  return exposure;
}

export function markWordReviewed(progress, id) {
  const study = progress.tracks ||= {};
  const track = study.study ||= {reviewedWordIds: [], lessonId: null};
  track.reviewedWordIds ||= [];
  if (!track.reviewedWordIds.includes(id)) track.reviewedWordIds.push(id);
  return progress;
}
